import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import { exhaustMap, map, catchError, tap, switchMap, mergeMap } from "rxjs/operators";

import * as ContractsActionTypes from "../../actions/contracts/contracts.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { AppState } from "../../reducers";
import { ContractsService } from "src/app/shared/services/contracts/contracts.service";
import { ProductGroupService } from "src/app/shared/services/products/productGroup/productGroup.service";
import { SitesService } from "src/app/shared/services/sites/sites.service";
import { LimitsService } from "src/app/shared/services/limits/limits.service";
import { CardsService } from "src/app/shared/services/cards/cards.service";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

@Injectable()
export class ContractsEffects {
  // Get Contracts Plafond Rules
  getPlafondRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getPlafondRules),
      exhaustMap((action) =>
        this.contractsService.getPlafondRules(action.pageReq).pipe(
          map((result) =>
            ContractsActionTypes.loadPlafondRules({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.errorLoadPlafondRule",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Contracts Plafond Measurements
  getPlafondMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getPlafondMeasurements),
      exhaustMap((action) =>
        this.contractsService.getPlafondMeasurements(action.pageReq).pipe(
          map((result) =>
            ContractsActionTypes.loadPlafondMeasurements({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.errorLoadPlafondMeasurement",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Create Contract
  createContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.createContract),
      exhaustMap((action) =>
        this.contractsService.createContract(action).pipe(
          mergeMap((result) => [
            ContractsActionTypes.resetCreation(),
            ContractsActionTypes.getContract({
              contractCode: result?.result?.code,
            })
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.contractCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Contracts.contractCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Update Contract
  updateContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.updateContract),
      exhaustMap((action) =>
        this.contractsService.updateContract(action).pipe(
          map((result) => {
            return ContractsActionTypes.getContract({
              contractCode: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.contractUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Contracts.contractUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Contract
  getContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getContract),
      exhaustMap((action) =>
        this.contractsService.getContract(action).pipe(
          mergeMap((result) => [
            ContractsActionTypes.setContract({ contract: result }),
            ContractsActionTypes.setLoadingContractsEndState(),
          ]
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.ContractLoadingError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Contract List
  getContractList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getContractList),
      switchMap((action) =>
        this.contractsService
          .getContractsList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ContractsActionTypes.loadContractList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ContractsActionTypes.loadContractList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Contracts.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  //Contract Excel List
  getContractExcelList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getContractExcelList),
      switchMap((action) =>
        this.contractsService
          .getContractsList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ContractsActionTypes.loadContractExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ContractsActionTypes.loadContractExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Contracts.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Contracts Table Config
  getContractTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getContractTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(
            action.options.code,
            action.options.settingType
          )
          .pipe(
            map((result) =>
            ContractsActionTypes.loadContractTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ContractsActionTypes.loadContractTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Contracts Table Config
  saveContractTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.saveContractTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveSuccess",
                  SnackbarType.SUCCESS
                )
              )
            ]
          ),
          catchError((error: any) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getContractCardList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getContractCardList),
      exhaustMap((action) =>
        this.cardsService
          .getFiltredCardList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.container
          )
          .pipe(
            map((result) =>
              ContractsActionTypes.loadContractCardsList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ContractsActionTypes.loadContractCardsList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj("Cards.loadError", SnackbarType.ERROR)
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  // // Get Contract Movements
  getContractMovementsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getContractMovementList),
      exhaustMap((action) =>
        this.contractsService
          .getContractMovements(
            action.itemCode,
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs
          )
          .pipe(
            map((result) =>
              ContractsActionTypes.loadContractMovementList({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ContractsActionTypes.loadContractMovementList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Contracts.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Contracts Product Groups
  getProductGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getProductGroups),
      exhaustMap((action) =>
        this.productGroupService.getProductGroup(action.pageReq).pipe(
          map((result) =>
            ContractsActionTypes.loadProductGroups({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.errorLoadProductGroups",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getProductGroupsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getProductGroupsList),
      exhaustMap((action) =>
        this.productGroupService
          .getProductGroupList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ContractsActionTypes.loadProductGroups({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ContractsActionTypes.setLoadingContractsEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Contracts.errorLoadProductGroups",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Delete Contract ProductGroup
  deleteContractProductGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.deleteContractProductGroup),
      exhaustMap((action) =>
        this.contractsService.deleteContractProductGroups(action).pipe(
          map((result) => ContractsActionTypes.loadContracts(null)),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.contractUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Contracts Sites
  getSitesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getSitesList),
      exhaustMap((action) =>
        this.sitesService
          .getSites(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ContractsActionTypes.loadSitesList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ContractsActionTypes.setLoadingContractsEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Contracts.errorLoadSiteLists",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Contracts limit Intervals
  getLimitIntervals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getPlafondRules),
      exhaustMap((action) =>
        this.limitsService.getLimitIntervals(action.pageReq).pipe(
          map((result) =>
            ContractsActionTypes.loadLimitIntervals({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.errorLoadLimitInterval",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Contracts Limit Measurements
  getLimitMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.getLimitMeasurements),
      exhaustMap((action) =>
        this.limitsService.getLimitMeasurements(action.pageReq).pipe(
          map((result) =>
            ContractsActionTypes.loadLimitMeasurements({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.errorLoadLimitMeasurement",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Save Contract Limits
  saveContractLimits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.saveContractLimits),
      exhaustMap((action) =>
        this.contractsService.saveContractLimits(action).pipe(
          map((result) => ContractsActionTypes.loadContracts(null)),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Contracts.contractCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Delete Contract Limits
  deleteContractLimits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.deleteContractLimits),
      exhaustMap((action) =>
        this.contractsService.deleteContractLimits(action).pipe(
          map((result) => ContractsActionTypes.loadContracts(null)),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.contractUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  resetCardLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.resetContractLimit),
      exhaustMap((action) =>
        this.contractsService.resetContractLimit(action).pipe(
          map((result) => {
            this.store$.dispatch(
              ContractsActionTypes.getContract({
                contractCode: action.contractCode,
              })
            );
            return ContractsActionTypes.setLoadingContractsEndState();
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.limitResetSuccess",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.limitResetFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  resetCardPlafond$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.resetContractPlafond),
      exhaustMap((action) =>
        this.contractsService.resetContractPlafond(action.contractCode).pipe(
          map((result) => {
            this.store$.dispatch(
              ContractsActionTypes.getContract({
                contractCode: action.contractCode,
              })
            );
            return ContractsActionTypes.setLoadingContractsEndState();
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.plafondResetSuccess",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.errorResetPlafond",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Update Contract Product Groups
  updateContractProductGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.updateContractProductGroups),
      exhaustMap((action) =>
        this.contractsService.updateContractProductGroup(action).pipe(
          map((result) => {
            this.store$.dispatch(
              ContractsActionTypes.getContract({
                contractCode: action.contractCode,
              })
            );
            return ContractsActionTypes.setLoadingContractsEndState();
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.contractUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.contractUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Update Contract Limits
  updateContractLimits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContractsActionTypes.updateContractLimits),
      exhaustMap((action) =>
        this.contractsService.updateContractLimits(action).pipe(
          map((result) => {
            this.store$.dispatch(
              ContractsActionTypes.getContract({
                contractCode: action.contractCode,
              })
            );
            return ContractsActionTypes.setLoadingContractsEndState();
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.contractUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ContractsActionTypes.setLoadingContractsEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Contracts.contractUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private contractsService: ContractsService,
    private productGroupService: ProductGroupService,
    private sitesService: SitesService,
    private translate: TranslateService,
    private limitsService: LimitsService,
    private cardsService: CardsService,
    private tableConfigService: TableConfigService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
